// src/pages/SettingsSecurity.jsx
//
// Settings > Security: two-step verification for the signed-in user. Every
// user can manage their own (any role); the server acts on the token's user
// only, so nothing here is a security boundary. Mounted at /settings/security.
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card.jsx';
import { AlertTriangle, Loader2, ShieldOff, KeyRound } from 'lucide-react';
import { apiCall } from '../utils/api';
import { mfaErrorMessage } from '../lib/mfa';
import SecurityStatusCard from '../components/mfa/SecurityStatusCard';
import MfaSetupModal from '../components/mfa/MfaSetupModal';
import MfaConfirmModal from '../components/mfa/MfaConfirmModal';
import RecoveryCodesModal from '../components/mfa/RecoveryCodesPanel';

export function RecoveryUsedNotice({ remaining }) {
  return (
    <div className="flex items-start gap-2 p-3 mb-6 bg-amber-500/10 border border-amber-500/30 rounded-lg" role="status">
      <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
      <p className="text-sm text-amber-200">
        You signed in with a recovery code, so that code no longer works.
        {Number.isFinite(remaining) ? ` ${remaining} left.` : ''}
        {' '}
        If you lost your phone, set two-step verification up again on your new one
        and make a fresh set of codes.
      </p>
    </div>
  );
}

// The signed-in email, for the recovery codes file. Storage can be unavailable
// (blocked site data, some private windows); the page still works without it.
function readAccount() {
  try {
    return window.localStorage.getItem('user_email') || '';
  } catch {
    return '';
  }
}

async function postJson(path, body) {
  try {
    const res = await apiCall(path, { method: 'POST', body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    return res.ok ? { data } : { error: mfaErrorMessage(res.status, data.detail) };
  } catch (err) {
    console.error('Two-step request failed', err);
    return { error: 'Network error. Please check your connection and try again.' };
  }
}

export default function SettingsSecurity() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // 'setup' | 'turnOff' | 'regenerate' | 'cancelSetup' | null
  const [dialog, setDialog] = useState(null);
  const [newCodes, setNewCodes] = useState(null);
  const [recoveryNotice] = useState(location.state?.recoveryCodeUsed ? location.state : null);
  const account = readAccount();

  // Drop the one-time notice from history so a refresh does not show it again.
  useEffect(() => {
    if (location.state?.recoveryCodeUsed) navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const loadStatus = useCallback(async () => {
    setError('');
    try {
      const res = await apiCall('/auth/mfa/status');
      if (res.ok) {
        setStatus(await res.json());
      } else {
        setError('Could not load your two-step verification status.');
      }
    } catch (err) {
      console.error('Two-step status load error', err);
      setError('Network error loading your two-step verification status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const closeAndReload = () => { setDialog(null); loadStatus(); };

  const handleTurnOff = async (password, code) => {
    const { error: message } = await postJson('/auth/mfa/disable', { password, code });
    if (!message) closeAndReload();
    return message || null;
  };

  const handleCancelSetup = async (password) => {
    const { error: message } = await postJson('/auth/mfa/disable', { password, code: '' });
    if (!message) closeAndReload();
    return message || null;
  };

  const handleRegenerate = async (password, code) => {
    const { data, error: message } = await postJson('/auth/mfa/recovery-codes/regenerate', { password, code });
    if (!message) {
      setDialog(null);
      setNewCodes(data.recovery_codes || []);
    }
    return message || null;
  };

  return (
    <div className="flex-1 max-w-3xl">
      <h2 className="text-2xl font-semibold text-white mb-1">Security</h2>
      <p className="text-sm text-[#a0a0a0] mb-6">
        Protect your Connex sign-in. These settings apply to your account only.
      </p>

      {recoveryNotice && <RecoveryUsedNotice remaining={recoveryNotice.recoveryCodesRemaining} />}

      {loading ? (
        <Card>
          <CardContent className="p-6 flex items-center justify-center text-[#a0a0a0]">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading security settings...
          </CardContent>
        </Card>
      ) : (
        <SecurityStatusCard
          status={status}
          error={error}
          onSetup={() => setDialog('setup')}
          onCancelSetup={() => setDialog('cancelSetup')}
          onTurnOff={() => setDialog('turnOff')}
          onRegenerate={() => setDialog('regenerate')}
        />
      )}

      <div className="mt-6 p-4 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a]">
        <h3 className="text-sm font-semibold text-white mb-2">Good to know</h3>
        <ul className="text-sm text-[#a0a0a0] space-y-2 list-disc list-inside">
          <li>Any authenticator app that supports time-based codes works.</li>
          <li>Turning two-step verification on or off, or making new recovery codes, signs you out everywhere else.</li>
          <li>Recovery codes are shown once and stored only as fingerprints. Keep them in a password manager.</li>
          <li>Lost your phone and your codes? Contact Connex support to have it reset.</li>
        </ul>
      </div>

      {dialog === 'setup' && (
        <MfaSetupModal account={account} onCancel={closeAndReload} onDone={closeAndReload} />
      )}
      {dialog === 'turnOff' && (
        <MfaConfirmModal
          icon={ShieldOff}
          tone="red"
          title="Turn off two-step verification?"
          subtitle="Sign-in will ask for your password only. Your recovery codes stop working."
          confirmText="Turn off"
          confirmVariant="danger"
          onSubmit={handleTurnOff}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog === 'regenerate' && (
        <MfaConfirmModal
          icon={KeyRound}
          title="Make new recovery codes?"
          subtitle="Your current recovery codes stop working as soon as the new ones are made."
          confirmText="Make new codes"
          onSubmit={handleRegenerate}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog === 'cancelSetup' && (
        <MfaConfirmModal
          icon={ShieldOff}
          tone="amber"
          title="Cancel setup?"
          subtitle="The setup key you started with stops working. You can start again any time."
          confirmText="Cancel setup"
          requireCode={false}
          onSubmit={handleCancelSetup}
          onCancel={() => setDialog(null)}
        />
      )}
      {newCodes && (
        <RecoveryCodesModal
          codes={newCodes}
          account={account}
          onClose={() => { setNewCodes(null); loadStatus(); }}
        />
      )}
    </div>
  );
}
